import React, { FC, useEffect, useMemo } from 'react';
import { Box, Fade, keyframes, Paper, Portal, Typography } from '@mui/material';
import { FallingLeaves } from './FallingLeaves';
import { AutumnPumpkin } from './AutumnPumpkin';
import { AutumnColors } from './autumnColors';

export const GREETINGS = [
  { title: 'God høst! 🍂', text: 'Vi i Museum24-teamet ønsker dere en fin høst.' },
  { title: 'Bø! 🎃', text: 'Gresskaret ønsker deg en riktig god høst.' },
  { title: 'Du fant gresskaret!', text: 'Tre klikk, og høsten faller over Museum24. Ha en fin dag!' },
  { title: 'Høsthilsen fra Museum24', text: 'Ta en kopp kaffe, se på løvet som faller, og publiser noe fint.' },
  { title: 'Gresskaret sier hei', text: 'Visste du at gresskar botanisk sett er et bær? Nå vet du det.' },
];

let timesShown = 0;

/** The first greeting is always shown the first time the easter egg opens; after that one is picked at random. */
export const pickGreeting = (): (typeof GREETINGS)[number] => {
  const greeting = timesShown === 0 ? GREETINGS[0] : GREETINGS[Math.floor(Math.random() * GREETINGS.length)];
  timesShown += 1;
  return greeting;
};

/** Test helper so every test starts with the first greeting. */
export const resetGreetings = (): void => {
  timesShown = 0;
};

const wobble = keyframes`
  0%, 100% { transform: rotate(-6deg) scale(1); }
  50% { transform: rotate(6deg) scale(1.08); }
`;

export const EASTER_EGG_DURATION_MS = 8000;

export interface AutumnEasterEggProps {
  onDone: () => void;
  /** Override for tests. */
  durationMs?: number;
}

/** Full-screen falling leaves and a greeting card. Click anywhere on the card, or wait, to dismiss. */
export const AutumnEasterEgg: FC<AutumnEasterEggProps> = ({ onDone, durationMs = EASTER_EGG_DURATION_MS }) => {
  const greeting = useMemo(pickGreeting, []);

  useEffect(() => {
    const timeout = window.setTimeout(onDone, durationMs);
    return () => window.clearTimeout(timeout);
  }, [onDone, durationMs]);

  return (
    <Portal>
      <FallingLeaves count={45} minDuration={4} maxDuration={8} zIndex={2000} />
      <Fade in appear timeout={600}>
        <Box
          data-testid='AutumnEasterEgg'
          sx={{
            position: 'fixed',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 2001,
            pointerEvents: 'none',
          }}>
          <Paper
            elevation={8}
            role='status'
            onClick={onDone}
            sx={{
              pointerEvents: 'auto',
              cursor: 'pointer',
              px: 4,
              py: 3,
              maxWidth: 420,
              textAlign: 'center',
              borderRadius: 3,
              border: `2px solid ${AutumnColors.PUMPKIN}`,
              background: `linear-gradient(180deg, #fff 0%, ${AutumnColors.HEADER_TINT} 100%)`,
            }}>
            <Box
              sx={{
                display: 'inline-block',
                animation: `${wobble} 1.2s ease-in-out infinite`,
                '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
              }}>
              <AutumnPumpkin size={72} lit />
            </Box>
            <Typography variant='h6' sx={{ mt: 1, fontWeight: 700, color: AutumnColors.PUMPKIN_DARK }}>
              {greeting.title}
            </Typography>
            <Typography variant='body1' sx={{ mt: 0.5 }}>
              {greeting.text}
            </Typography>
            <Typography variant='caption' sx={{ display: 'block', mt: 1.5, opacity: 0.6 }}>
              Klikk for å lukke
            </Typography>
          </Paper>
        </Box>
      </Fade>
    </Portal>
  );
};

export default AutumnEasterEgg;
